import { inject, Injectable } from "@angular/core";
import { Auth } from "@angular/fire/auth";
import { ActivatedRoute, Router } from "@angular/router";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { IEntity, IEntityProgressMap, UUID, } from "src/app/core/interfaces";
import { ErrorHandlerService } from "src/app/core/services/firebase-error-handler.service";
import { initialState, SecureFileStateModel, } from "./secure-file-model.interface";
import { SecureFile } from "./secure-file.actions";
import { SecureFileService } from "./secure-file.service";
import { MatSnackBar } from "@angular/material/snack-bar";

@State<SecureFileStateModel>({
  name: "secureFile",
  defaults: { ...initialState },
})
@Injectable()
export class SecureFileState {
  private auth: Auth = inject(Auth);
  private errorHandler = inject(ErrorHandlerService);
  private router: Router = inject(Router);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private secureFileService = inject(SecureFileService);
  private snackbar = inject(MatSnackBar);

  @Selector()
  static getEntities(state: SecureFileStateModel) {
    return state.entities;
  }

  @Selector()
  static getEntity(state: SecureFileStateModel) {
    return (entityId: UUID): IEntity | null =>
      state.entities.find((entity) => entity.id === entityId) ?? null;
  }

  @Selector()
  static getIsEntityLoading(state: SecureFileStateModel) {
    return state.isEntityLoading;
  }

  @Selector()
  static getIsEntityOwnersLoading(state: SecureFileStateModel) {
    return state.isEntityOwnersLoading;
  }

  @Selector()
  static getIsEntityProgressLoading(state: SecureFileStateModel) {
    return state.isEntityProgressLoading;
  }

  @Action(SecureFile.Reset)
  reset(ctx: StateContext<SecureFileStateModel>) {
    ctx.setState({ ...initialState });
  }

  @Action(SecureFile.GetEntity)
  async getEntity(
    { dispatch }: StateContext<SecureFileStateModel>,
    { entityId }: SecureFile.GetEntity
  ) {
    try {
      const entity = await this.secureFileService.getEntity(entityId);
      dispatch(new SecureFile.GetEntitySuccess(entity));
    } catch (error) {
      dispatch(new SecureFile.GetEntityFail(error));
    }
  }

  @Action(SecureFile.GetEntitySuccess)
  getEntitySuccess(
    { getState, patchState }: StateContext<SecureFileStateModel>,
    { entity }: SecureFile.GetEntitySuccess
  ) {
    const existingEntities = getState().entities;
    patchState({
      entities: [
        ...existingEntities.filter(
          (existingEntity) => existingEntity.id !== entity.id
        ),
        entity,
      ],
    });
  }

  @Action(SecureFile.GetEntityFail)
  getEntityFail(
    { error }: SecureFile.GetEntityFail
  ) {
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.GetEntities)
  async getEntities(
    { dispatch }: StateContext<SecureFileStateModel>
  ) {
    try {
      const userId = this.auth.currentUser?.uid;
      if (!userId) {
        throw new Error("User is not authenticated");
      }
      const entities = await this.secureFileService.getEntities(userId);
      dispatch(new SecureFile.GetEntitiesSuccess(entities));
    } catch (error) {
      dispatch(new SecureFile.GetEntitiesFail(error));
    }
  }

  @Action(SecureFile.GetEntitiesSuccess)
  getEntitiesSuccess(
    { patchState }: StateContext<SecureFileStateModel>,
    { entities }: SecureFile.GetEntitiesSuccess
  ) {
    patchState({ entities });
  }

  @Action(SecureFile.GetEntitiesFail)
  getEntitiesFail(
    { error }: SecureFile.GetEntitiesFail
  ) {
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.Create)
  async create(
    { dispatch, patchState }: StateContext<SecureFileStateModel>,
    { entity }: SecureFile.Create
  ) {
    patchState({
      isEntityLoading: true,
    });
    const createEntityOperation = this.secureFileService.createEntityOperation("createEntity");
    try {
      const newEntity = await createEntityOperation(entity);
      dispatch(new SecureFile.CreateSuccess(newEntity));
    } catch (error) {
      dispatch(new SecureFile.CreateFail(error));
    }
  }

  @Action(SecureFile.CreateSuccess)
  createSuccess(
    { patchState, getState }: StateContext<SecureFileStateModel>,
    { entity }: SecureFile.CreateSuccess
  ) {
    patchState({
      entities: [...getState().entities, entity],
      isEntityLoading: false,
    });
  }

  @Action(SecureFile.CreateFail)
  createFail(
    { patchState }: StateContext<SecureFileStateModel>,
    { error }: SecureFile.CreateFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.Update)
  async update(
    { dispatch, patchState }: StateContext<SecureFileStateModel>,
    { entity }: SecureFile.Update
  ) {
    patchState({
      isEntityLoading: true,
    });
    const updateEntityOperation = this.secureFileService.createEntityOperation("updateEntity");
    try {
      const updatedEntity = await updateEntityOperation(entity);
      dispatch(new SecureFile.UpdateSuccess(updatedEntity));
    } catch (error) {
      dispatch(new SecureFile.UpdateFail(error));
    }
  }

  @Action(SecureFile.UpdateSuccess)
  updateSuccess(
    { patchState, getState }: StateContext<SecureFileStateModel>,
    { updatedEntity, message }: SecureFile.UpdateSuccess,
  ) {
    const entities = getState().entities
    patchState({
      entities: entities.map((existingEntity) =>
        existingEntity.id === updatedEntity.id ? updatedEntity : existingEntity
      ),
      isEntityLoading: false,
    });
    if (message) {
      this.snackbar.open(message, "Dismiss", { duration: 5000 });
    }
  }

  @Action(SecureFile.UpdateFail)
  updateFail(
    { patchState }: StateContext<SecureFileStateModel>,
    { error }: SecureFile.UpdateFail,
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.SubmitToFinCEN)
  async submitToFinCEN(
    { dispatch }: StateContext<SecureFileStateModel>,
    { entityId, consentData }: SecureFile.SubmitToFinCEN
  ) {
    try {
      const updatedEntity = await this.secureFileService.submitToFinCEN(
        entityId,
        consentData
      );
      dispatch(new SecureFile.UpdateSuccess(updatedEntity));
      dispatch(new SecureFile.SubmitToFinCENSuccess());
    } catch (error: unknown) {
      dispatch(new SecureFile.SubmitToFinCENFail(error));
    }
  }

  @Action(SecureFile.SubmitToFinCENSuccess)
  async submitToFinCENSuccess() {
    const params = { ...this.activatedRoute?.snapshot?.queryParams };
    this.router.navigate(["payments/thank-you"], { queryParams: params });
  }

  @Action(SecureFile.SubmitToFinCENFail)
  async submitToFinCENFail(
    { error }: SecureFile.SubmitToFinCENFail
  ) {
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.Delete)
  async delete(
    { dispatch, patchState }: StateContext<SecureFileStateModel>,
    { entityId }: SecureFile.Delete
  ) {
    patchState({
      isEntityLoading: true,
    });
    const deleteEntityOperation = this.secureFileService.createEntityOperation("deleteEntity");
    try {
      await deleteEntityOperation(entityId);
      dispatch(new SecureFile.DeleteSuccess(entityId));
    } catch (error) {
      dispatch(new SecureFile.DeleteFail(error));
    }
  }

  @Action(SecureFile.DeleteSuccess)
  deleteSuccess(
    { patchState, getState }: StateContext<SecureFileStateModel>,
    { entityId }: SecureFile.DeleteSuccess
  ) {
    patchState({
      entities: getState().entities.filter(
        (entity) => entity.id !== entityId
      ),
      isEntityLoading: false,
    });
  }

  @Action(SecureFile.DeleteFail)
  deleteFail(
    { patchState }: StateContext<SecureFileStateModel>,
    { error }: SecureFile.DeleteFail
  ) {
    patchState({
      isEntityLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.AddOwnerToEntity)
  async addOwnerToEntity(
    { dispatch, patchState }: StateContext<SecureFileStateModel>,
    { entityId, ownerId, boiType }: SecureFile.AddOwnerToEntity
  ) {
    patchState({
      isEntityOwnersLoading: true,
    });
    const addOwnerToEntityOperation = this.secureFileService.createEntityOperation("addOwnerToEntity");
    try {
      const entity = await addOwnerToEntityOperation(
        entityId,
        ownerId,
        boiType
      );
      dispatch(new SecureFile.AddOwnerToEntitySuccess(entity));
    } catch (error) {
      dispatch(new SecureFile.AddOwnerToEntityFail(error));
    }
  }

  @Action(SecureFile.AddOwnerToEntitySuccess)
  addOwnerToEntitySuccess(
    { getState, patchState }: StateContext<SecureFileStateModel>,
    { entity }: SecureFile.AddOwnerToEntitySuccess
  ) {
    const entities = getState().entities;
    patchState({
      entities: entities.map((existingEntity) =>
        existingEntity.id === entity.id ? entity : existingEntity
      ),
      isEntityOwnersLoading: false,
    });
  }

  @Action(SecureFile.AddOwnerToEntityFail)
  addOwnerToEntityFail(
    { patchState }: StateContext<SecureFileStateModel>,
    { error }: SecureFile.AddOwnerToEntityFail
  ) {
    patchState({
      isEntityOwnersLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.RemoveOwnerFromEntity)
  async removeOwnerFromEntity(
    { dispatch, patchState }: StateContext<SecureFileStateModel>,
    { entityId, ownerId, boiType }: SecureFile.RemoveOwnerFromEntity
  ) {
    patchState({
      isEntityOwnersLoading: true,
    });
    const removeOwnerFromEntityOperation = this.secureFileService.createEntityOperation("removeOwnerFromEntity");
    try {
      const entity = await removeOwnerFromEntityOperation(
        entityId,
        ownerId,
        boiType
      );
      dispatch(new SecureFile.RemoveOwnerFromEntitySuccess(entity));
    } catch (error) {
      dispatch(new SecureFile.RemoveOwnerFromEntityFail(error));
    }
  }

  @Action(SecureFile.RemoveOwnerFromEntitySuccess)
  removeOwnerFromEntitySuccess(
    { getState, patchState }: StateContext<SecureFileStateModel>,
    { entity }: SecureFile.RemoveOwnerFromEntitySuccess
  ) {
    const entities = getState().entities;
    patchState({
      entities: entities.map((existingEntity) =>
        existingEntity.id === entity.id ? entity : existingEntity
      ),
      isEntityOwnersLoading: false,
    });
  }

  @Action(SecureFile.RemoveOwnerFromEntityFail)
  removeOwnerFromEntityFail(
    { patchState }: StateContext<SecureFileStateModel>,
    { error }: SecureFile.RemoveOwnerFromEntityFail
  ) {
    patchState({
      isEntityOwnersLoading: false,
    });
    this.errorHandler.handleError(error);
  }

  @Action(SecureFile.GetTotalEntityProgress)
  async getTotalEntityProgress(
    { dispatch, patchState }: StateContext<SecureFileStateModel>,
    { entityId, ownerEntityIds, isNeedUpdateEntityInFirestore }: SecureFile.GetTotalEntityProgress
  ) {
    patchState({
      isEntityProgressLoading: true
    });
    const getTotalEntityProgressOperation = this.secureFileService.createEntityOperation("getTotalEntityProgress");
    try {
      const totalEntityProgress = isNeedUpdateEntityInFirestore
        ? await getTotalEntityProgressOperation(entityId, null)
        : await this.secureFileService.getTotalEntityProgress(entityId, null);

      const ownerEntityTotalProgressPromises = ownerEntityIds.map(async (entityId) => {
        const { totalPercentFilled } = await getTotalEntityProgressOperation(entityId, null);
        return [entityId, totalPercentFilled] as [UUID, number];
      });
      const ownerEntityProgresses = await Promise.all(ownerEntityTotalProgressPromises);
      const ownerEntityProgressMap = ownerEntityProgresses.reduce((acc, [entityId, progress]) => {
        acc[entityId] = progress;
        return acc;
      }, {} as IEntityProgressMap);

      dispatch(new SecureFile.GetTotalEntityProgressSuccess(
        entityId,
        totalEntityProgress,
        ownerEntityProgressMap
      ));
    } catch (error) {
      dispatch(new SecureFile.GetTotalEntityProgressFail(error));
    }
  }

  @Action(SecureFile.GetTotalEntityProgressSuccess)
  getTotalEntityProgressSuccess(
    { getState, patchState }: StateContext<SecureFileStateModel>,
    { entityId, totalProgress, ownerEntityProgressMap }: SecureFile.GetTotalEntityProgressSuccess
  ) {
    const fullOwnerEntityProgressMap = {
      ...ownerEntityProgressMap,
      [entityId]: totalProgress.totalPercentFilled
    };

    const entities = getState().entities;
    const updatedEntities = entities.map((entity) => {
      return Object.prototype.hasOwnProperty.call(fullOwnerEntityProgressMap, entity.id)
        ? { ...entity, progress: fullOwnerEntityProgressMap[entity.id] }
        : entity;
    });

    patchState({
      entities: updatedEntities,
      isEntityProgressLoading: false,
    });
  }

  @Action(SecureFile.GetTotalEntityProgressFail)
  getTotalEntityProgressFail(
    { patchState }: StateContext<SecureFileStateModel>,
    { error }: SecureFile.GetTotalEntityProgressFail
  ) {
    patchState({
      isEntityProgressLoading: false,
    });
    this.errorHandler.handleError(error);
  }



  @Action(SecureFile.UpdateCapTable)
  async updateCapTable(
    { patchState, dispatch }: StateContext<SecureFileStateModel>,
    { entityId, shareTypeChanges }: SecureFile.UpdateCapTable
  ): Promise<void> {
    patchState({
      isEntityLoading: true,
    });

    const updateShareTypesOperation = this.secureFileService.createEntityOperation("updateShareTypes");

    try {
      const updatedEntity = await updateShareTypesOperation(entityId, shareTypeChanges);
      dispatch(new SecureFile.UpdateSuccess(updatedEntity, "Share Types were successfully updated"));
    } catch (error) {
      dispatch(new SecureFile.UpdateFail(error));
    }
  }

  @Action(SecureFile.ManageCapTableRecords)
  async addCapTableRecord(
    { patchState, dispatch }: StateContext<SecureFileStateModel>,
    { entityId, shareTypeId, shareRecordChanges }: SecureFile.ManageCapTableRecords
  ): Promise<void> {
    patchState({
      isEntityLoading: true,
    });

    const manageCapTableRecordsOperation =
      this.secureFileService.createEntityOperation("manageCapTableRecords");

    try {
      const updatedEntity = await manageCapTableRecordsOperation(entityId, shareTypeId, shareRecordChanges);

      dispatch(new SecureFile.UpdateSuccess(updatedEntity, "Cap Table Records were successfully updated"));
    } catch (error) {
      dispatch(new SecureFile.UpdateFail(error));
    }
  }

  @Action(SecureFile.UpdateDocumentTypes)
  async updateDocumentTypes (
    { dispatch, patchState }: StateContext< SecureFileStateModel>,
    { entityId, updatedDocTypes }: SecureFile.UpdateDocumentTypes
  ): Promise<void> {
    try {
      patchState({
        isEntityLoading: true,
      })
      const updatedEntity = await this.secureFileService.updateDocumentTypes(
        entityId,
        updatedDocTypes
      );

      dispatch(new SecureFile.UpdateSuccess(updatedEntity, "Document Types were successfully updated"));
    } catch (e) {
      dispatch(new SecureFile.UpdateFail(e));
    }
  }

  @Action(SecureFile.AddDocumentsToType)
  async addDocumentToType (
    { dispatch }: StateContext< SecureFileStateModel>,
    { entityId, documentTypeId, documents }: SecureFile.AddDocumentsToType
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureFileService.addDocumentToType(
        entityId,
        documentTypeId,
        documents
      );

      dispatch(new SecureFile.UpdateSuccess(updatedEntity, "Documents were successfully added"));
    } catch (e) {
      dispatch(new SecureFile.UpdateFail(e));
    }
  }

  @Action(SecureFile.MoveDocumentBetweenTypes)
  async moveDocumentBetweenTypes (
    { dispatch }: StateContext< SecureFileStateModel>,
    { entityId, fromDocumentTypeId, toDocumentTypeId, documentId }: SecureFile.MoveDocumentBetweenTypes
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureFileService.moveDocumentBetweenTypes(
        entityId,
        fromDocumentTypeId,
        toDocumentTypeId,
        documentId
      );

      dispatch(new SecureFile.UpdateSuccess(updatedEntity, "Document was successfully moved"));
    } catch (e) {
      dispatch(new SecureFile.UpdateFail(e));
    }
  }

  @Action(SecureFile.RemoveDocumentFromType)
  async removeDocumentFromType (
    { dispatch }: StateContext< SecureFileStateModel>,
    { entityId, documentTypeId, document }: SecureFile.RemoveDocumentFromType
  ): Promise<void> {
    try {
      const updatedEntity = await this.secureFileService.removeDocumentFromType(
        entityId,
        documentTypeId,
        document
      );

      dispatch(new SecureFile.UpdateSuccess(updatedEntity, "Document was successfully removed"));
    } catch (e) {
      dispatch(new SecureFile.UpdateFail(e));
    }
  }
}
